import { useCallback, useEffect, useSyncExternalStore } from 'react';

type AppearanceMode = 'light' | 'dark' | 'system';

const LS_KEY = 'appearance_mode';

function getStoredMode(): AppearanceMode {
  const stored = localStorage.getItem(LS_KEY);
  if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  return 'light';
}

function resolveEffective(mode: AppearanceMode): 'light' | 'dark' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
}

function applyToDOM(mode: AppearanceMode) {
  const effective = resolveEffective(mode);
  document.documentElement.classList.toggle('dark', effective === 'dark');
}

// Simple external store for cross-component reactivity
let listeners: Array<() => void> = [];
let currentMode: AppearanceMode = getStoredMode();

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
}

function getSnapshot(): AppearanceMode {
  return currentMode;
}

function setMode(mode: AppearanceMode) {
  currentMode = mode;
  localStorage.setItem(LS_KEY, mode);
  applyToDOM(mode);
  listeners.forEach(l => l());
}

// Initialize on module load
applyToDOM(currentMode);

export function useAppearanceMode() {
  const mode = useSyncExternalStore(subscribe, getSnapshot);

  // Listen for cross-tab localStorage changes
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY) {
        const newMode = (e.newValue as AppearanceMode) || 'light';
        currentMode = newMode;
        applyToDOM(newMode);
        listeners.forEach(l => l());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (mode !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyToDOM('system');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mode]);

  const isDark = resolveEffective(mode) === 'dark';

  return {
    mode,
    setMode: useCallback((m: AppearanceMode) => setMode(m), []),
    isDark,
  };
}
